import { useEffect, useMemo, useRef, useState } from "react";
import {
    ANALYTICS_EVENTS,
    trackAnalyticsEvent,
} from "../../../../../../analytics";
import { formatPhoneForDisplay } from "../../../../../../utils";

type Client = {
    id: number | string;
    fullname?: string;
    phone?: string | number;
};

type DealsMap = Record<string | number, any[]>;

type DealStatus = {
    id: number;
    code?: string;
    name: string;
};

type UseAttachCalcToDealModalArgs = {
    isOpen: boolean;
    onClose: () => void;
    sharingId: number | string | null;

    clientsPending: boolean;
    clientsLoadingMore: boolean;
    clientsError: boolean;
    clients: Client[];
    clientsMeta: {
        perPage: number;
        nextCursor: string | null;
        hasMore: boolean;
    };

    dealsMap: DealsMap;
    dealsPending: boolean;
    dealsError: boolean;
    dealsLoadingId: number | string | null;

    dealStatuses: DealStatus[];
    dealStatusesPending: boolean;

    getClientsBase: (payload?: {
        sortBy?: "fullname";
        perPage?: number;
        cursor?: string | null;
    }) => any;
    getClientDeals: (payload: { clientId: number | string }) => any;

    createClientInBase: (payload: {
        fullname: string;
        phone: string;
        telegram?: string;
    }) => any;

    createClientDeal: (payload: {
        dealStatusId: number;
        clientId: number;
        query: string;
    }) => any;

    getDealStatuses: () => any;

    attachClientDealAttachment: (payload: {
        dealId: number | string;
        attachmentableId: number | string;
        attachmentableType: string;
    }) => any;

    getDealAttachments: (payload: {
        clientId: number | string;
        dealId: number | string;
    }) => any;
};

type Option = {
    value: number | string;
    label: string;
};

const CREATE_CLIENT_VALUE = "__create_client__";
const LOAD_MORE_CLIENTS_VALUE = "__load_more_clients__";
const CREATE_DEAL_VALUE = "__create_deal__";

type Mode = "select" | "createClient" | "createDeal";

const isFulfilled = (action: any) => typeof action?.type === "string" && action.type.endsWith("/fulfilled");

export const useAttachCalcToDealModal = (args: UseAttachCalcToDealModalArgs) => {
    const {
        isOpen,
        onClose,
        sharingId,
        clientsPending,
        clientsLoadingMore,
        clients,
        clientsMeta,
        dealsMap,
        dealsPending,
        dealsLoadingId,

        dealStatuses,
        dealStatusesPending,

        getClientsBase,
        getClientDeals,
        createClientInBase,
        createClientDeal,
        getDealStatuses,

        attachClientDealAttachment,
        getDealAttachments,
    } = args;

    const [clientId, setClientId] = useState<string | number | null>(null);
    const [dealId, setDealId] = useState<string | number | null>(null);

    const [attachPending, setAttachPending] = useState(false);

    // 🆕 режимы + формы
    const [mode, setMode] = useState<Mode>("select");

    const [createClientPending, setCreateClientPending] = useState(false);
    const [createDealPending, setCreateDealPending] = useState(false);

    const [newClientFullName, setNewClientFullName] = useState("");
    const [newClientPhone, setNewClientPhone] = useState("");
    const [createClientErrorMessage, setCreateClientErrorMessage] = useState("");

    const [newDealQuery, setNewDealQuery] = useState("");
    const clientsRequestedForOpenRef = useRef(false);

    // при открытии — подгружаем клиентов/статусы, при закрытии — сбрасываем стейты
    useEffect(() => {
        if (isOpen) {
            if (!clientsRequestedForOpenRef.current) {
                clientsRequestedForOpenRef.current = true;
                getClientsBase({ sortBy: "fullname", perPage: 100, cursor: null });
            }
            if (!dealStatusesPending && (!dealStatuses || dealStatuses.length === 0)) getDealStatuses();
        }

        if (!isOpen) {
            clientsRequestedForOpenRef.current = false;
            setClientId(null);
            setDealId(null);
            setAttachPending(false);

            setMode("select");

            setCreateClientPending(false);
            setCreateDealPending(false);

            setNewClientFullName("");
            setNewClientPhone("");
            setCreateClientErrorMessage("");

            setNewDealQuery("");
        }
    }, [
        isOpen,
        clientsPending,
        clients.length,
        getClientsBase,
        dealStatusesPending,
        dealStatuses?.length,
        getDealStatuses,
    ]);

    // при выборе клиента — подгружаем его сделки (если ещё не загружены)
    useEffect(() => {
        if (!clientId) return;

        const clientKey = String(clientId);

        const hasDealsEntry = Object.prototype.hasOwnProperty.call(dealsMap, clientKey);
        const isLoading = dealsPending && String(dealsLoadingId) === clientKey;

        if (!hasDealsEntry && !isLoading) {
            getClientDeals({ clientId });
        }
    }, [clientId, dealsMap, dealsPending, dealsLoadingId, getClientDeals]);

    const dealsForSelectedClient: any[] = useMemo(
        () => (clientId != null ? dealsMap[clientId] || [] : []),
        [clientId, dealsMap],
    );

    const hasDealsEntryForSelectedClient: boolean = useMemo(() => {
        if (clientId == null) return false;
        const clientKey = String(clientId);
        return Object.prototype.hasOwnProperty.call(dealsMap, clientKey);
    }, [clientId, dealsMap]);

    const clientOptions: Option[] = useMemo(() => {
        const base = clients.map((c) => ({
            value: c.id,
            label: `${c.fullname || "Без имени"}${
                c.phone ? ` (${formatPhoneForDisplay(c.phone)})` : ""
            }`,
        }));

        // 🆕 пункт "Новый клиент"
        const options = [
            ...base,
            { value: CREATE_CLIENT_VALUE, label: "➕ Новый клиент" },
        ];

        if (clientsMeta.hasMore && clientsMeta.nextCursor) {
            options.push({
                value: LOAD_MORE_CLIENTS_VALUE,
                label: clientsLoadingMore
                    ? "Загружаем клиентов…"
                    : "Показать ещё клиентов",
            });
        }

        return options;
    }, [clients, clientsMeta, clientsLoadingMore]);

    const dealOptions: Option[] = useMemo(() => {
        const base = dealsForSelectedClient.map((d: any) => ({
            value: d.id,
            label: `#${d.id} · ${d.dealStatus?.name || "Без статуса"}${d.query ? ` · ${d.query}` : ""}`,
        }));

        // 🆕 пункт "Новая сделка"
        return [
            ...base,
            { value: CREATE_DEAL_VALUE, label: "➕ Новая сделка" },
        ];
    }, [dealsForSelectedClient]);

    const handleClientChange = (opt: Option | null) => {
        const value = opt?.value ?? null;

        if (value === CREATE_CLIENT_VALUE) {
            setMode("createClient");
            setDealId(null);
            return;
        }

        if (value === LOAD_MORE_CLIENTS_VALUE) {
            if (!clientsLoadingMore && clientsMeta.nextCursor) {
                getClientsBase({
                    sortBy: "fullname",
                    perPage: clientsMeta.perPage || 100,
                    cursor: clientsMeta.nextCursor,
                });
            }
            return;
        }

        setMode("select");
        setClientId(value);
        setDealId(null);
    };

    const handleDealChange = (opt: Option | null) => {
        const value = opt?.value ?? null;

        if (value === CREATE_DEAL_VALUE) {
            setMode("createDeal");
            return;
        }

        setMode("select");
        setDealId(value);
    };

    const cancelCreateClient = () => {
        setMode("select");
        setNewClientFullName("");
        setNewClientPhone("");
        setCreateClientErrorMessage("");
    };

    const cancelCreateDeal = () => {
        setMode("select");
        setNewDealQuery("");
    };

    // берём “дефолтный” статус для сделки
    const defaultDealStatusId = useMemo(() => {
        if (!dealStatuses?.length) return null;

        // если есть code=new/created и т.п. — можно улучшить матчинг, но пока берём первый
        const preferred =
            dealStatuses.find((s) => (s.code || "").toLowerCase() === "new") ||
            dealStatuses[0];

        return preferred?.id ?? null;
    }, [dealStatuses]);

    const handleCreateClient = async () => {
        if (!newClientFullName.trim() || !newClientPhone.trim()) {
            setCreateClientErrorMessage("Укажите ФИО и телефон клиента.");
            return;
        }

        try {
            setCreateClientPending(true);
            setCreateClientErrorMessage("");

            const action = await createClientInBase({
                fullname: newClientFullName.trim(),
                phone: newClientPhone.trim(),
            });

            if (!isFulfilled(action)) {
                setCreateClientErrorMessage(
                    action?.payload?.message ||
                    "Не удалось создать клиента. Попробуйте ещё раз.",
                );
                return;
            }

            const payload = action.payload;
            const created = payload?.data ?? payload ?? null;

            if (created?.id) {
                setClientId(created.id);
                setDealId(null);
                setMode("select");

                // на всякий: если у клиента ещё нет entry — загрузим
                getClientDeals({ clientId: created.id });
            }
        } catch (e) {
            console.error("Ошибка при создании клиента", e);
        } finally {
            setCreateClientPending(false);
        }
    };

    const handleCreateDeal = async () => {
        if (!clientId) return;
        if (!defaultDealStatusId) return; // статусы ещё не загружены

        try {
            setCreateDealPending(true);

            // Автозаголовок, если пусто
            const query =
                newDealQuery.trim() ||
                `Расчёт от ${new Date().toLocaleDateString()}`;

            const action = await createClientDeal({
                clientId: Number(clientId),
                dealStatusId: Number(defaultDealStatusId),
                query,
            });

            if (!isFulfilled(action)) return;

            const payload = action.payload;
            const createdDeal = payload?.deal ?? null;

            if (createdDeal?.id) {
                setDealId(createdDeal.id);
                setMode("select");
            }
        } catch (e) {
            console.error("Ошибка при создании сделки", e);
        } finally {
            setCreateDealPending(false);
        }
    };

    const handleAttach = async () => {
        if (!clientId || !dealId || !sharingId) return;

        try {
            setAttachPending(true);
            trackAnalyticsEvent(ANALYTICS_EVENTS.CALCULATION_ATTACH_REQUESTED, {
                share_id: sharingId,
                client_id: clientId,
                deal_id: dealId,
            });

            const action = await attachClientDealAttachment({
                dealId,
                attachmentableId: sharingId,
                attachmentableType: "sharedCalc",
            });
            if (!isFulfilled(action)) {
                throw new Error("Attachment request was rejected");
            }

            await getDealAttachments({
                clientId,
                dealId,
            });

            trackAnalyticsEvent(ANALYTICS_EVENTS.CALCULATION_ATTACHED_TO_DEAL, {
                share_id: sharingId,
                client_id: clientId,
                deal_id: dealId,
            });
            onClose();
        } catch (e) {
            trackAnalyticsEvent(ANALYTICS_EVENTS.CALCULATION_ATTACH_FAILED, {
                share_id: sharingId,
                client_id: clientId,
                deal_id: dealId,
                failure_reason: e instanceof Error ? e.name : "unknown",
            });
            console.error("Ошибка при прикреплении расчёта к сделке", e);
        } finally {
            setAttachPending(false);
        }
    };

    return {
        clientId,
        dealId,

        attachPending,
        createClientPending,
        createDealPending,

        mode,

        newClientFullName,
        newClientPhone,
        createClientErrorMessage,
        setNewClientFullName,
        setNewClientPhone,

        newDealQuery,
        setNewDealQuery,

        clientOptions,
        dealOptions,

        dealsForSelectedClient,
        hasDealsEntryForSelectedClient,

        defaultDealStatusId,

        handleClientChange,
        handleDealChange,

        cancelCreateClient,
        cancelCreateDeal,

        handleCreateClient,
        handleCreateDeal,

        handleAttach,
    };
};
